/**
 * Customers (owner product review §25): operational table with enum/date filters, filter chips, server
 * sort and pagination. Search stays EXACT-match (internal id, mobile or national ID — audited); filters
 * never accept free text. Money columns render «—» until the ledger phases (never a fake zero).
 */
import {
  ActiveFilters,
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  formatRelative,
  PageHeader,
  Pagination,
  SelectField,
  StateView,
  TextField,
  type DataTableColumn,
  type TableSort,
} from '@hopex/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { dt, type AdminUserRow, type CustomerPage, type CustomerRow } from '../api.ts';
import { ACCOUNT_STATUS, LEVEL_LABEL } from '../lib/labels.ts';
import { Remote, useRemote } from '../lib/remote.tsx';

type Filters = {
  status: string;
  kyc: string;
  totp: string;
  bank: string;
  created: string;
  active: string;
};
const EMPTY: Filters = { status: '', kyc: '', totp: '', bank: '', created: '', active: '' };

const OPTIONS: Record<
  keyof Filters,
  { label: string; options: { value: string; label: string }[] }
> = {
  status: {
    label: 'وضعیت حساب',
    options: [
      { value: '', label: 'همه' },
      { value: 'ACTIVE', label: 'فعال' },
      { value: 'LOCKED', label: 'قفل' },
      { value: 'SUSPENDED', label: 'تعلیق‌شده' },
    ],
  },
  kyc: {
    label: 'سطح احراز',
    options: [
      { value: '', label: 'همه' },
      { value: 'NONE', label: 'سطح ۰' },
      { value: 'KYC1', label: 'سطح ۱' },
      { value: 'KYC2', label: 'سطح ۲' },
    ],
  },
  totp: {
    label: 'ورود دومرحله‌ای',
    options: [
      { value: '', label: 'همه' },
      { value: 'ON', label: 'فعال' },
      { value: 'OFF', label: 'غیرفعال' },
    ],
  },
  bank: {
    label: 'بانک تأییدشده',
    options: [
      { value: '', label: 'همه' },
      { value: 'YES', label: 'دارد' },
      { value: 'NO', label: 'ندارد' },
    ],
  },
  created: {
    label: 'تاریخ ثبت‌نام',
    options: [
      { value: '', label: 'همه' },
      { value: '1', label: '۲۴ ساعت اخیر' },
      { value: '7', label: '۷ روز اخیر' },
      { value: '30', label: '۳۰ روز اخیر' },
    ],
  },
  active: {
    label: 'آخرین فعالیت',
    options: [
      { value: '', label: 'همه' },
      { value: '1', label: '۲۴ ساعت اخیر' },
      { value: '7', label: '۷ روز اخیر' },
      { value: '30', label: '۳۰ روز اخیر' },
    ],
  },
};

const dash = (
  <span className="text-fg-subtle" aria-label="داده‌ای نیست">
    —
  </span>
);

function columns(): DataTableColumn<CustomerRow>[] {
  return [
    {
      id: 'user',
      header: 'کاربر',
      isRowHeader: true,
      minWidth: '12rem',
      cell: (u) => (
        <span className="flex flex-col">
          <Link
            to="/users/$id"
            params={{ id: u.id }}
            className="font-semibold text-fg hover:underline"
            data-customer-link
          >
            {u.displayName ?? 'بدون نام'}
          </Link>
          <bdi dir="ltr" className="font-mono text-[11px] text-fg-subtle" data-public-id>
            {u.publicId}
          </bdi>
        </span>
      ),
    },
    {
      id: 'phone',
      header: 'موبایل',
      cell: (u) => (
        <bdi dir="ltr" className="font-mono">
          {u.phoneMasked ?? '—'}
        </bdi>
      ),
    },
    {
      id: 'kyc',
      header: 'احراز',
      cell: (u) => (
        <Badge tone={u.kycLevel === 'NONE' ? 'neutral' : 'positive'}>
          {LEVEL_LABEL[u.kycLevel]}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'وضعیت',
      cell: (u) => (
        <Badge tone={u.status === 'ACTIVE' ? 'positive' : 'negative'}>
          {ACCOUNT_STATUS[u.status] ?? u.status}
        </Badge>
      ),
    },
    {
      id: 'totp',
      header: 'دومرحله‌ای',
      cell: (u) => (u.totpEnabled ? 'فعال' : u.totpRequired ? 'اجباری' : 'غیرفعال'),
    },
    { id: 'banks', header: 'بانک تأییدشده', numeric: true, cell: (u) => u.verifiedBanks },
    { id: 'balance', header: 'موجودی', numeric: true, cell: (u) => u.balanceToman ?? dash },
    {
      id: 'deposits',
      header: 'کل واریز',
      numeric: true,
      hideBelow: 1280,
      cell: (u) => u.lifetimeDepositsToman ?? dash,
    },
    {
      id: 'withdrawals',
      header: 'کل برداشت',
      numeric: true,
      hideBelow: 1280,
      cell: (u) => u.lifetimeWithdrawalsToman ?? dash,
    },
    {
      id: 'created',
      header: 'ثبت‌نام',
      sortable: true,
      cell: (u) => <span title={dt(u.createdAt)}>{dt(u.createdAt)}</span>,
    },
    {
      id: 'active',
      header: 'آخرین فعالیت',
      sortable: true,
      cell: (u) => (u.lastActiveAt ? formatRelative(u.lastActiveAt) : '—'),
    },
    {
      id: 'flags',
      header: 'پرچم‌ها',
      cell: (u) =>
        u.status !== 'ACTIVE' || u.totpRequired ? (
          <span className="flex gap-1">
            {u.status !== 'ACTIVE' && <Badge tone="negative">محدود</Badge>}
            {u.totpRequired && !u.totpEnabled && <Badge tone="accent">دومرحله‌ای اجباری</Badge>}
          </span>
        ) : (
          dash
        ),
    },
  ];
}

function sinceIso(days: string): string | undefined {
  return days ? new Date(Date.now() - Number(days) * 86_400_000).toISOString() : undefined;
}

export function CustomersPage() {
  const navigate = useNavigate();
  const [f, setF] = useState<Filters>(EMPTY);
  const [sort, setSort] = useState<TableSort>({ column: 'created', direction: 'descending' });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState<string | null>(null);

  const path = useMemo(() => {
    const p = new URLSearchParams();
    if (f.status) p.set('status', f.status);
    if (f.kyc) p.set('kyc', f.kyc);
    if (f.totp) p.set('totp', f.totp);
    if (f.bank) p.set('bank', f.bank);
    const from = sinceIso(f.created);
    if (from) p.set('createdFrom', from);
    if (f.active) p.set('activeWithinDays', f.active);
    p.set('sort', sort.column === 'active' ? 'active' : 'created');
    p.set('dir', sort.direction === 'ascending' ? 'asc' : 'desc');
    p.set('page', String(page));
    p.set('pageSize', '25');
    return `/customers?${p.toString()}`;
  }, [f, sort, page]);
  // Created-date windows move with the clock; refetch only when the user changes a filter.
  const r = useRemote<CustomerPage>(query === null ? path : null);
  const s = useRemote<{ items: AdminUserRow[] }>(
    query === null ? null : `/users?q=${encodeURIComponent(query)}`,
  );

  const set = (k: keyof Filters, v: string) => {
    setF((x) => ({ ...x, [k]: v }));
    setPage(1);
  };
  const chips = (Object.keys(f) as (keyof Filters)[])
    .filter((k) => f[k])
    .map((k) => ({
      id: k,
      label: `${OPTIONS[k].label}: ${OPTIONS[k].options.find((o) => o.value === f[k])?.label ?? f[k]}`,
    }));

  return (
    <AppContainer>
      <PageHeader title="کاربران" />
      <Card className="mb-4 flex flex-col gap-3 p-4" data-customer-filters>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(q.trim() || null);
          }}
        >
          <div className="w-full max-w-xs">
            <TextField label="شناسه‌ی هوپکس، موبایل یا کد ملی" value={q} onChange={setQ} ltr />
          </div>
          <Button type="submit" variant="secondary">
            جستجو
          </Button>
          {query !== null && (
            <Button variant="ghost" onPress={() => (setQ(''), setQuery(null))}>
              نمایش همه
            </Button>
          )}
          <p className="m-0 ms-auto self-center text-[11.5px] text-fg-subtle">
            جستجو فقط با تطابق کامل انجام و ثبت می‌شود.
          </p>
        </form>
        {query === null && (
          <>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
              {(Object.keys(OPTIONS) as (keyof Filters)[]).map((k) => (
                <SelectField
                  key={k}
                  label={OPTIONS[k].label}
                  value={f[k]}
                  onChange={(v) => set(k, v)}
                  options={OPTIONS[k].options}
                />
              ))}
            </div>
            <ActiveFilters
              items={chips}
              onRemove={(id) => set(id as keyof Filters, '')}
              onClear={() => (setF(EMPTY), setPage(1))}
            />
          </>
        )}
      </Card>

      {query !== null ? (
        <Remote r={s}>
          {(d) => (
            <DataTable<AdminUserRow>
              label="نتیجه‌ی جستجو"
              rows={d.items}
              density="compact"
              onRowAction={(id) => void navigate({ to: '/users/$id', params: { id } })}
              columns={[
                {
                  id: 'name',
                  header: 'کاربر',
                  isRowHeader: true,
                  cell: (u) => (
                    <Link
                      to="/users/$id"
                      params={{ id: u.id }}
                      className="font-semibold hover:underline"
                    >
                      {u.displayName ?? 'بدون نام'}
                    </Link>
                  ),
                },
                {
                  id: 'phone',
                  header: 'موبایل',
                  cell: (u) => (
                    <bdi dir="ltr" className="font-mono">
                      {u.phoneMasked ?? '—'}
                    </bdi>
                  ),
                },
                {
                  id: 'status',
                  header: 'وضعیت',
                  cell: (u) => ACCOUNT_STATUS[u.status] ?? u.status,
                },
                { id: 'created', header: 'ثبت‌نام', cell: (u) => dt(u.createdAt) },
              ]}
              empty={<StateView compact kind="empty" title="کاربری با این مشخصات پیدا نشد" />}
            />
          )}
        </Remote>
      ) : r.error ? (
        <Remote r={r}>{() => null}</Remote>
      ) : (
        <>
          <DataTable<CustomerRow>
            label="فهرست کاربران"
            rows={r.data?.items ?? []}
            loading={r.loading && !r.data}
            density="compact"
            stickyFirst
            maxHeight="calc(100dvh - 330px)"
            sort={sort}
            onSortChange={(s2) => (setSort(s2), setPage(1))}
            onRowAction={(id) => void navigate({ to: '/users/$id', params: { id } })}
            columns={columns()}
            empty={
              <StateView
                compact
                kind="empty"
                title={
                  chips.length ? 'کاربری با این فیلترها پیدا نشد' : 'هنوز کاربری ثبت‌نام نکرده است'
                }
              />
            }
          />
          {r.data && (
            <Pagination
              page={r.data.page}
              pageSize={r.data.pageSize}
              total={r.data.total}
              onPage={setPage}
            />
          )}
        </>
      )}
    </AppContainer>
  );
}
